import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { parseMutationError } from '../api/errors';
import {
  useAddTextVersion,
  useConsentTypes,
  useCreateConsentType,
  useUpdateConsentType,
  type ConsentType,
} from '../api/consent';
import {
  NON_DEFAULT_LOCALES,
  type LocalizedString,
  type SupportedLocale,
  joinFromColumns,
  localizedFromTextVersions,
  splitForColumns,
} from '../api/localized-string';
import { LocalizedStringInput } from '../components/localized-string-input';

export function ProductConsentPage() {
  const { id = '' } = useParams<{ id: string }>();
  const typesQuery = useConsentTypes(id); // defaults (no orgId)
  const create = useCreateConsentType(id);
  const update = useUpdateConsentType(id);
  const addVersion = useAddTextVersion(id);
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState<LocalizedString>({ default: '' });
  const [blocking, setBlocking] = useState(false);
  const [text, setText] = useState<LocalizedString>({ default: '' });
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<ConsentType | null>(null);
  const [editName, setEditName] = useState<LocalizedString>({ default: '' });
  const [editBlocking, setEditBlocking] = useState(false);
  const [editText, setEditText] = useState<LocalizedString>({ default: '' });
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);

  // product defaults = those with organisation_id === null
  const defaults = (typesQuery.data ?? []).filter((t) => t.organisation_id === null);

  function submit() {
    setError(null);
    const { default: display_name, translations: display_name_translations } =
      splitForColumns(displayName);
    const { default: textBody, translations: text_translations } = splitForColumns(text);
    create.mutate(
      {
        product_id: id,
        code,
        display_name,
        display_name_translations,
        blocking,
        text: textBody,
        text_translations,
        locale: 'en',
      },
      {
        onSuccess: () => {
          notifications.show({ color: 'green', message: 'Consent type created.' });
          setCode('');
          setDisplayName({ default: '' });
          setBlocking(false);
          setText({ default: '' });
        },
        onError: (err) => setError(parseMutationError(err, 'Could not create consent type.')),
      },
    );
  }

  function startEdit(t: ConsentType) {
    setEditing(t);
    setEditName(joinFromColumns(t.display_name, t.display_name_translations));
    setEditBlocking(t.blocking);
    setEditText(localizedFromTextVersions(t.text_versions));
    openEdit();
  }

  function saveEdit() {
    if (!editing) return;
    const editingType = editing;
    const { default: display_name, translations: display_name_translations } =
      splitForColumns(editName);
    const nameChanged =
      display_name !== editingType.display_name ||
      JSON.stringify(display_name_translations ?? {}) !==
        JSON.stringify(editingType.display_name_translations ?? {});
    const currentText = localizedFromTextVersions(editingType.text_versions);
    const locales: SupportedLocale[] = ['en', ...NON_DEFAULT_LOCALES];

    // Each new text version's number is computed server-side from the current
    // max, so the per-locale writes must be serialised — firing them
    // concurrently would race on the [type, org, version] unique constraint.
    const save = async () => {
      if (nameChanged || editBlocking !== editingType.blocking) {
        await update.mutateAsync({
          id: editingType.id,
          display_name,
          display_name_translations,
          blocking: editBlocking,
        });
      }
      for (const locale of locales) {
        const nextBody =
          locale === 'en' ? editText.default : (editText.translations?.[locale] ?? '');
        const prevBody =
          locale === 'en' ? currentText.default : (currentText.translations?.[locale] ?? '');
        // Only publish non-empty, changed bodies; clearing a saved
        // translation is a deliberate no-op (see design doc).
        if (nextBody !== '' && nextBody !== prevBody) {
          await addVersion.mutateAsync({ id: editingType.id, body: nextBody, locale });
        }
      }
    };

    save()
      .then(() => {
        notifications.show({ color: 'green', message: 'Consent type updated.' });
        closeEdit();
      })
      .catch(() => notifications.show({ color: 'red', message: 'Could not save consent type.' }));
  }

  if (typesQuery.isPending) return <Text p="md">Loading…</Text>;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Default consents</Title>
        <Button variant="subtle" component={Link} to={`/products/${id}`}>
          Back to product
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Card withBorder padding="lg">
        {defaults.length === 0 ? (
          <Text c="dimmed">No default consents for this product yet.</Text>
        ) : (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Flavour</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {defaults.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>{t.code}</Table.Td>
                  <Table.Td>{t.display_name}</Table.Td>
                  <Table.Td>
                    <Badge color={t.blocking ? 'red' : 'gray'} variant="light">
                      {t.blocking ? 'blocking' : 'non-blocking'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button size="xs" variant="subtle" onClick={() => startEdit(t)}>
                      Edit
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Card withBorder padding="lg" maw={520}>
        <Stack gap="sm">
          <Title order={5}>Add default consent</Title>
          <TextInput label="Code" value={code} onChange={(e) => setCode(e.currentTarget.value)} />
          <LocalizedStringInput
            label="Display name"
            value={displayName}
            onChange={setDisplayName}
          />
          <LocalizedStringInput
            label="Consent text"
            description="Shown to patients. Required."
            multiline
            value={text}
            onChange={setText}
          />
          <Switch
            label="Blocking (patient cannot proceed without it)"
            checked={blocking}
            onChange={(e) => setBlocking(e.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <Button
              onClick={submit}
              loading={create.isPending}
              disabled={!code || !displayName.default || !text.default}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Card>

      <Modal opened={editOpen} onClose={closeEdit} title={`Edit ${editing?.code ?? ''}`}>
        <Stack>
          <LocalizedStringInput
            label="Display name"
            value={editName}
            onChange={setEditName}
            required
          />
          <Switch
            label="Blocking"
            checked={editBlocking}
            onChange={(e) => setEditBlocking(e.currentTarget.checked)}
          />
          <LocalizedStringInput
            label="Consent text"
            multiline
            value={editText}
            onChange={setEditText}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeEdit}>
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              loading={update.isPending || addVersion.isPending}
              disabled={!editName.default || !editText.default}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
